import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Tooltip, Icon, Card, Row, Col, Badge, Dropdown, Menu, Button } from 'antd';

const tableStyles = `
@keyframes ct2-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
@keyframes ct2-beat   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.22)} }
@keyframes ct2-bounce { 0%,100%{transform:translateY(0) scaleY(1)} 40%{transform:translateY(-5px) scaleY(1.05)} 70%{transform:translateY(2px) scaleY(0.96)} }
@keyframes ct2-shake  { 0%,100%{transform:rotate(0)} 20%{transform:rotate(-14deg)} 40%{transform:rotate(14deg)} 60%{transform:rotate(-8deg)} 80%{transform:rotate(8deg)} }
@keyframes ct2-glow   { 0%,100%{box-shadow:0 0 0 0 var(--gc,rgba(24,144,255,.35))} 50%{box-shadow:0 0 0 8px rgba(0,0,0,0)} }
@keyframes ct2-spin   { 0%{transform:rotate(0)} 100%{transform:rotate(180deg)} }

.ct2-row-stripe td{background:#f7f9ff!important}
.ct2-badge{display:inline-flex;align-items:center;gap:4px;padding:2px 9px;border-radius:10px;font-size:11px;font-weight:700;white-space:nowrap}
.ct2-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0}
.ct2-pending{background:#fff7e6;border:1px solid #ffd591;color:#d46b08}
.ct2-pending .ct2-dot{background:#fa8c16;box-shadow:0 0 5px #fa8c16}
.ct2-rejected{background:#fff1f0;border:1px solid #ffa39e;color:#cf1322}
.ct2-rejected .ct2-dot{background:#ff4d4f;box-shadow:0 0 5px #ff4d4f}
.ct2-recommended{background:#e6fffb;border:1px solid #87e8de;color:#08979c}
.ct2-recommended .ct2-dot{background:#13c2c2;box-shadow:0 0 5px #13c2c2}
.ct2-approved{background:#f6ffed;border:1px solid #b7eb8f;color:#389e0d}
.ct2-approved .ct2-dot{background:#52c41a;box-shadow:0 0 5px #52c41a}

.ct2-settings-btn{width:28px!important;height:28px!important;min-width:28px!important;padding:0!important;border-radius:7px!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;font-size:13px!important;border:1.5px solid #d6e8ff!important;background:#e6f7ff!important;color:#1890ff!important;transition:all .18s!important}
.ct2-settings-btn.open{background:#1890ff!important;color:#fff!important;border-color:#1890ff!important;box-shadow:0 3px 10px rgba(24,144,255,.35)!important}
.ct2-settings-btn.open .anticon-setting{animation:ct2-spin .4s cubic-bezier(.22,1,.36,1) forwards!important}
.ct2-action-menu.ant-dropdown-menu{border-radius:10px!important;border:1px solid #e6f0ff!important;box-shadow:0 8px 28px rgba(24,144,255,.12)!important;padding:5px!important;min-width:140px!important}
.ct2-action-menu .ant-dropdown-menu-item{border-radius:7px!important;padding:8px 12px!important;font-size:12px!important;font-weight:600!important;transition:background .15s,transform .12s!important}
.ct2-action-menu .ant-dropdown-menu-item:hover{transform:translateX(3px)!important;background:#e6f7ff!important;color:#1890ff!important}
`;

const StatusBadge = ({ status, comment }) => {
  const map = {
    'PENDING APPROVAL': { cls: 'ct2-pending',     label: 'PENDING'     },
    'REJECTED':         { cls: 'ct2-rejected',    label: 'REJECTED'    },
    'RECOMMENDED':      { cls: 'ct2-recommended', label: 'RECOMMENDED' },
    'APPROVED':         { cls: 'ct2-approved',    label: 'APPROVED'    },
  };
  const cfg = map[status] || { cls: 'ct2-pending', label: status };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
      <span className={`ct2-badge ${cfg.cls}`}><span className="ct2-dot" />{cfg.label}</span>
      {comment && <Tooltip title={comment}><Icon type="info-circle" theme="twoTone" style={{ fontSize:13 }} /></Tooltip>}
    </span>
  );
};

const ActionCell = ({ record, openModal }) => {
  const [open, setOpen] = useState(false);
  const menu = (
    <Menu className="ct2-action-menu">
      <Menu.Item key="view" onClick={() => openModal(record)}>
        <Icon type="profile" style={{ marginRight:8, color:'#1890ff' }} />View Details
      </Menu.Item>
    </Menu>
  );
  return (
    <Dropdown overlay={menu} trigger={['hover']} placement="bottomLeft"
      getPopupContainer={() => document.body} onVisibleChange={v => setOpen(v)}>
      <Button className={`ct2-settings-btn${open ? ' open' : ''}`} size="small" icon="setting" />
    </Dropdown>
  );
};

const CustomersTable = ({ tableData, openModal }) => {
  const stats = useMemo(() => {
    if (!tableData || !tableData.length) return { total:0, approved:0, pending:0, rejected:0 };
    return {
      total:    tableData.length,
      approved: tableData.filter(d => d.isApproved === 'APPROVED' || d.isApproved === 'RECOMMENDED').length,
      pending:  tableData.filter(d => d.isApproved === 'PENDING APPROVAL').length,
      rejected: tableData.filter(d => d.isApproved === 'REJECTED').length,
    };
  }, [tableData]);

  const cardStyle = { borderRadius:10, boxShadow:'0 2px 12px rgba(24,144,255,0.08)', border:'1px solid #e6f0ff', background:'#fff', height:'100%' };
  const statCards = [
    { title:'Total',    value:stats.total,    icon:'team',         color:'#1890ff', bg:'#e6f7ff', gc:'rgba(24,144,255,0.3)' },
    { title:'Approved', value:stats.approved, icon:'check-circle', color:'#52c41a', bg:'#f6ffed', gc:'rgba(82,196,26,0.3)'  },
    { title:'Pending',  value:stats.pending,  icon:'clock-circle', color:'#fa8c16', bg:'#fff7e6', gc:'rgba(250,140,22,0.3)' },
    { title:'Rejected', value:stats.rejected, icon:'close-circle', color:'#ff4d4f', bg:'#fff1f0', gc:'rgba(255,77,79,0.3)'  },
  ];

  const columns = [
    { key:'actions', title:'', align:'center', width:46, render: record => <ActionCell record={record} openModal={openModal} /> },
    {
      key:'isApproved', title:'STATUS', align:'center', width:150,
      render: data => <StatusBadge status={data.isApproved} comment={data.comment} />,
      filters: [
        { text:'PENDING APPROVAL', value:'PENDING APPROVAL' },
        { text:'REJECTED',         value:'REJECTED'         },
        { text:'RECOMMENDED',      value:'RECOMMENDED'      },
        { text:'APPROVED',         value:'APPROVED'         },
      ],
      onFilter: (value, record) => record.isApproved.indexOf(value) === 0,
      sorter: (a, b) => (a.isApproved || '').localeCompare(b.isApproved || ''),
      defaultSortOrder:'descend', sortDirections:['descend'],
    },
    { key:'bi_name',    dataIndex:'bi_name',    title:'COMPANY NAME', align:'center' },
    { key:'bi_address', dataIndex:'bi_address', title:'ADDRESS',      align:'center', width:280 },
    { key:'bi_tel',     dataIndex:'bi_tel',     title:'TEL. NO.',     align:'center' },
    {
      key:'bi_email', dataIndex:'bi_email', title:'EMAIL', align:'center',
      render: val => val
        ? <a href={`mailto:${val}`} style={{ color:'#1890ff', fontWeight:500 }}>{val}</a>
        : <Icon type="ellipsis" style={{ color:'#d9d9d9' }} />,
    },
  ];

  return (
    <div style={{ padding:'8px 0' }}>
      <style>{tableStyles}</style>
      <Row gutter={[16,16]} style={{ marginBottom:20 }}>
        {statCards.map((card, idx) => (
          <Col key={idx} xs={12} sm={6}>
            <Card style={cardStyle} bodyStyle={{ padding:'18px 20px' }} bordered={false}>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ width:46, height:46, borderRadius:12, background:card.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, '--gc':card.gc, animation:'ct2-glow 2.4s ease-in-out infinite' }}>
                  <Icon type={card.icon} style={{ fontSize:20, color:card.color }} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:'#8c8c8c', fontWeight:500, letterSpacing:'.3px', whiteSpace:'nowrap' }}>{card.title.toUpperCase()}</div>
                  <div style={{ fontSize:24, fontWeight:700, color:card.color, lineHeight:1.2 }}>{card.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <Card bordered={false} style={{ borderRadius:10, boxShadow:'0 2px 12px rgba(24,144,255,0.07)', border:'1px solid #e6f0ff' }} bodyStyle={{ padding:'0 0 8px 0' }}>
        <div style={{ padding:'14px 20px 12px', borderBottom:'1px solid #f0f5ff', display:'flex', alignItems:'center', gap:8 }}>
          <Icon type="team" style={{ color:'#1890ff', fontSize:16, animation:'ct2-beat 1.8s ease-in-out infinite' }} />
          <span style={{ fontWeight:700, fontSize:13, color:'#262626', letterSpacing:'.5px' }}>CUSTOMERS</span>
          <Badge count={stats.total} style={{ backgroundColor:'#1890ff', fontSize:11, fontWeight:600 }} overflowCount={9999} />
          {stats.pending > 0 && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:10, marginLeft:4, background:'#fff7e6', border:'1px solid #ffd591', fontSize:11, fontWeight:700, color:'#d46b08' }}>
              <Icon type="clock-circle" style={{ fontSize:10 }} />{stats.pending} PENDING
            </span>
          )}
        </div>
        <Table columns={columns} dataSource={tableData} rowKey="id" size="middle" bordered style={{ margin:0 }}
          bodyStyle={{ overflowX:'auto' }}
          rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'ct2-row-stripe'}
          pagination={{ showSizeChanger:true, pageSizeOptions:['10','25','50'], showTotal:(total,range) => `Showing ${range[0]}-${range[1]} of ${total} customers`, style:{ padding:'8px 16px', marginBottom:0 } }}
        />
      </Card>
    </div>
  );
};

CustomersTable.propTypes = {
  tableData: PropTypes.arrayOf(PropTypes.object).isRequired,
  openModal: PropTypes.func.isRequired,
};
export default CustomersTable;